import { z } from 'zod';
/**
 * Computed plugin status. Derived at serialization time from
 * (deprecated_at, yanked_at); never persisted. Spec §4.2#2.
 */
export const pluginStatusSchema = z.enum(['active', 'deprecated', 'yanked']);
/**
 * Canonical plugin row. Spec §4.1, §4.3.
 */
export const pluginSchema = z.object({
    id: z.string().uuid(),
    name: z
        .string()
        .min(1)
        .max(100)
        .regex(/^[a-z0-9-]+$/, 'plugin name must be lowercase kebab-case'),
    displayName: z.string().nullable(),
    description: z.string().nullable(),
    currentVersion: z.string().nullable(),
    sourceId: z.string().uuid(),
    /**
     * Plugin folder path inside its source repo. `''` = repo-root plugin
     * (sync-repo supports plugins living at the repo root), so no `min(1)` —
     * consumers must treat empty string as "root", not as missing data.
     */
    sourcePath: z.string(),
    categoryId: z.string().uuid(),
    authorId: z.string().uuid(),
    teamId: z.string().uuid().nullable(),
    /** Computed at serialization — never stored. */
    status: pluginStatusSchema,
    /**
     * Computed at serialization — never stored. True iff the row's sync-repo
     * Hub PR is open but not yet merged (`hub_pr_url IS NOT NULL AND
     * hub_listed_at IS NULL` — the same predicate the admin hub-queue uses).
     * Consumers hide pending rows from the catalog (`?listed=true`) and badge
     * them on team/detail surfaces. Predicate rationale + truth table:
     * `marketplace-api/src/lib/hub-pending.ts` (and its spec).
     */
    hubPending: z.boolean(),
    deprecatedAt: z.string().datetime().nullable(),
    yankedAt: z.string().datetime().nullable(),
    yankedBy: z.string().uuid().nullable(),
    yankedReason: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/** Flat tag row — `plugin_tags` table. */
export const pluginTagSchema = z.object({
    pluginId: z.string().uuid(),
    tag: z
        .string()
        .min(1)
        .max(50)
        .regex(/^[a-z0-9-]+$/),
});
/** Skill surfaced for search. */
export const pluginSkillSchema = z.object({
    pluginId: z.string().uuid(),
    name: z.string().min(1),
    description: z.string().min(1),
});
/** Category lookup row. */
export const categorySchema = z.object({
    id: z.string().uuid(),
    name: z
        .string()
        .min(1)
        .regex(/^[a-z0-9/-]+$/, 'category names are lowercase, slash-separable'),
    displayName: z.string().min(1),
    description: z.string().nullable(),
});
/** Denormalized list item emitted by `GET /plugins`. */
export const pluginListItemSchema = pluginSchema.extend({
    tags: z.array(z.string()),
    category: z.object({ name: z.string(), displayName: z.string() }),
    team: z
        .object({ id: z.string().uuid(), name: z.string(), displayName: z.string().nullable() })
        .nullable(),
    author: z.object({
        id: z.string().uuid(),
        displayName: z.string().nullable(),
    }),
});
/** Detail payload — adds skills, readme, manifest source record. */
export const pluginDetailSchema = pluginListItemSchema.extend({
    readmeContent: z.string().nullable(),
    skills: z.array(z.object({ name: z.string(), description: z.string() })),
    sourceUrl: z.string().url().nullable(),
});
//# sourceMappingURL=plugin.js.map